import React, {CSSProperties, useEffect, useState, Fragment, Dispatch, SetStateAction, useMemo} from 'react'
import { Tab, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Spinner } from 'react-bootstrap'
import { AiOutlineClose } from 'react-icons/ai'

import { useColors } from 'app_hooks/useColors'
import { useTranslation } from 'react-i18next'
import shortId from 'shortid'
import {
    getIconFromTypeTabEdit,
    IAvailabilityRange,
    IFieldTabEditorState,
    IGameConfigEditor,
    IQuestionEditor,
    IQuestionEditorApi,
    IQuestionEditorState,
    ISettingsTabsEditorAcademicComponent,
    ISettingsTabsEditorAcademicComponentState,
    ITabEditorAcademicComponentState,
    scrollToPositionEditor,
    TypeCallbackFunctionEditor,
    TypeQuestionsCategoriesEditor,
    TypeStreamCallbackFunctionEditor ,
    TypeTabsEditor,
    searchFieldFromListEditor,
    TypeGroupsTabEditor,
    filterFieldsByModuleEditor,
    getDataTypeFieldEditor,
    IGroupFieldsTabEditor
} from '../containers/academicComponentEditorFunctions'
import './../academicComponentEditor.scss'
import SwitchComponent from 'app_components/commons/switch'
import RenderFieldByNameEditor from './renderFieldByNameEditor'

interface IAcademicComponentEditorLayoutProps {
    settings: ISettingsTabsEditorAcademicComponent
    open: boolean
    loading?: boolean
    hideClose?: boolean
    showSaveAll?: boolean
    disableAll?: boolean
    disableButtonSaveAll?: boolean
    disableEditDuration?: boolean
    automaticDuration?: number
    currentQuestion?: IQuestionEditor | IQuestionEditorState
    onClickSaveAll?: TypeCallbackFunctionEditor
    streamAllData?: TypeStreamCallbackFunctionEditor
    handleClose?: ()=>void
    onClickOpenPanel?: ()=>void
    setIsCharacterizationSurvey?: Dispatch<SetStateAction<boolean>>
}

interface IOptionSelect{
  label: string
  value: string
}

const AcademicComponentEditorLayout: React.FC<IAcademicComponentEditorLayoutProps> = (props) => {

    const {t} = useTranslation()
    const [colors] = useColors()
    const [invalidSaveAll, updateInvalidSaveAll] = useState(false)
    const [activeTab, updateActiveTab] = useState<{type: TypeTabsEditor, id: string}>({type: 'general', id: ''})
    const [settingTabs, updateSettingTabs] = useState<ISettingsTabsEditorAcademicComponentState>({tabs: []})
    const courseModeName = useMemo(() => {
      const advancedTab = settingTabs.tabs.find((tab) => tab.type === 'advanced')
      const courseMode = advancedTab?.fields?.find((field) => field.type === "course_modes")?.value
      return advancedTab?.courseModes?.find((course) => course._id === courseMode)?.name
    }, [settingTabs])
    const disabledFields = useMemo(() => {
      if (courseModeName !== 'Virtual') {
        return ['course_type']
      }
      return []
    }, [courseModeName])

    useEffect(() => {
      if (props.setIsCharacterizationSurvey) {
        const courseMode = settingTabs.tabs.find((tab) => tab.type === 'advanced')?.fields?.find((field) => field.type === "course_modes")?.value
        if (courseMode === 'characterization-survey') {
          props.setIsCharacterizationSurvey(true)
        } else {
          props.setIsCharacterizationSurvey(false)
        }
      }
    }, [settingTabs])

    // Servicios
    // const questionsService = new QuestionsService()

    const renderGroupVector: TypeGroupsTabEditor[] = [
      // 'rest',    // Este no lo renderizo porque no lleva el accordion
      'course_setting',
      'community',
      'game',
      'learning',
      'resources',
      'statistics',
      'virtual_tutoring'
    ]

    /**
     * @INFO Actualizar el state con los tabs
     */
    useEffect(() => {
        const tabsSetting = [...props.settings.tabs]
        const tabsSettingState: ITabEditorAcademicComponentState[] = []
        tabsSetting.forEach((tab, idx) => {
            // Crear nuevo tab
            const newTab: ITabEditorAcademicComponentState = {
                id: shortId.generate(),
                type: tab.type,
                default: tab.default,
                fields: [],
                questions: [],
                callback: tab.callback,
                invalid: true,
                idContent: tab.idContent ? tab.idContent : '',
                extraData: tab.extraData ? tab.extraData : {},
                hideSaveButton: tab.hideSaveButton ? true : false,
                hideNewQuestions: tab.hideNewQuestions,
                showExerciseEnded: tab.showExerciseEnded,
                courseModes: tab.courseModes,
                streamCallback: tab.streamCallback ? tab.streamCallback : undefined,
                onClickQuestion: tab.onClickQuestion,
                onClickCreateQuestion: tab.onClickCreateQuestion,
                onFinishReorder: tab.onFinishReorder,
                groupsField: renderGroupVector.reduce((acum: IGroupFieldsTabEditor[], item) => {
                  acum.push({
                    type: item,
                    id: shortId.generate(),
                    state: false,
                    open: true
                  })
                  return acum
                },[])
            }
            // Organizar los field de los tabs
            const fields = tab.fields.reduce((acum: IFieldTabEditorState[], field, idxField) => {
                const newField: IFieldTabEditorState = {
                    id: shortId.generate(),
                    keyApi: field.keyApi ? field.keyApi : field.type,
                    required: field.required !== undefined ? field.required : true,
                    options: field.options ? field.options : [],
                    type: field.type,
                    value: field.value ? field.value : '',
                    invalid: true,
                    idx: idxField
                }
                if(
                    getDataTypeFieldEditor(field.type) === 'boolean'
                ){
                    newField.value = field.value ? field.value : false
                }
                if(field.type === 'color'){
                    newField.value = field.value ? field.value : '#FF0000'
                }
                if(field.type === 'duration' || field.type === 'time_limit'){
                    newField.value = getDurationFormated(field.value ? field.value as number : 0)
                }
                // if(field.type === 'exercise_ended'){
                //     newField.value = (field.value as IStateExercise['exercise_ended'])?.status ? field.value : undefined
                // }
                acum.push({...newField})
                return acum
            }, [])
            // Si hay questions guardarlas
            if(tab.questions && tab.questions.length){
                const questions = tab.questions.reduce((acum: IQuestionEditorState[], question) => {
                    const newQuestion: IQuestionEditorState = {
                        id: shortId.generate(),
                        position: question.position,
                        selected: false,
                        question: question.question,
                        value: question.value
                    }
                    // Organizar los childs de las questions
                    if(question.childs){
                        const childsQuestion = question.childs.reduce((acum2: IQuestionEditorState[], child) => {
                            const newChildQuestion: IQuestionEditorState = {
                                id: shortId.generate(),
                                position: child.position,
                                selected: false,
                                question: child.question,
                                value: child.value
                            }
                            acum2.push(newChildQuestion)
                            return acum2
                        },[])
                        newQuestion.childs = childsQuestion
                    }
                    acum.push({...newQuestion})
                    return acum
                },[])
                newTab.questions = [...questions]
            }
            // Guardar los field en el tab
            newTab.fields = [...fields]
            // Pongo la tab por defecto
            if(newTab.default){
                updateActiveTab({type: newTab.type, id: newTab.id})
            }
            tabsSettingState.push({...newTab})
        })
        // Colocar la pregunta seleccionada
        let resp = updateCurrentQuestion(
          props.currentQuestion,
          {
            tabs: [...tabsSettingState],
          }
        )
        // Verificar estado del switch en el grupo de campos
        resp = resp.tabs.reduce((acum: ISettingsTabsEditorAcademicComponentState, itemTab, idxTab) => {
            itemTab.groupsField.forEach(group => {
                acum = validateSwitchStateGroup({
                    idxTab,
                    idGroup:group.id,
                    _aux: {...acum}
                })
            })
            return acum
        }, {...resp})
        // Validación de campos
        validateTabs({...resp})
    }, [props.settings])

    /**
     * @INFO Actualizar la información de la pregunta seleccionada
     */
    useEffect(()=>{
        updateSettingTabs(updateCurrentQuestion(props.currentQuestion))
        updateActiveTab(activeTab)
    },[props.currentQuestion])

    /**
     * @INFO Actualizar la información de la duración del video
     */
    useEffect(() => {
      if (props.automaticDuration !== undefined){
        onUpdateDurationFromAutomaticDuration(props.automaticDuration)
      }
    }, [props.automaticDuration])

    /**
     * @INFO Validar si todos los campos CheckBox del grupo están marcados para actualizar el switch
     * @param options
     */
    const validateSwitchStateGroup = (options: {idxTab: number, idGroup: string, _aux?: ISettingsTabsEditorAcademicComponentState}) => {
      const { idxTab, idGroup } = options
      let aux = {...settingTabs}
      if(options._aux){
        aux = {...options._aux}
      }
      const idxGroup = aux.tabs[idxTab].groupsField.findIndex(item => item.id === idGroup)
      if(idxGroup >= 0){
        const fieldsGroup = filterFieldsByModuleEditor(
          aux.tabs[idxTab].fields,
          aux.tabs[idxTab].groupsField[idxGroup].type
        )
        if(fieldsGroup.length){
          const newState = fieldsGroup.reduce((acum: boolean, item) => {
            if(!aux.tabs[idxTab].fields[item.idx].value){
              acum = false
            }
            return acum
          }, true)
          aux.tabs[idxTab].groupsField[idxGroup].state = newState
        }
      }
      return {...aux}
    }

    /**
     * @INFO Actualizar valor de la duración desde los props
     * @param duration numero de segundos de la duración
     */
    const onUpdateDurationFromAutomaticDuration = (duration: number) => {
      const aux = {...settingTabs}
      const indexOfTab = aux.tabs.findIndex((tab: any) => tab.type === 'general')
      if (indexOfTab !== -1) {
        const indexOfField = aux.tabs[indexOfTab].fields.findIndex((field: any) => field.keyApi === 'duration')
        if (indexOfField !== -1) {
          aux.tabs[indexOfTab].fields[indexOfField].value = getDurationFormated(duration)
          validateTabs(aux, indexOfTab)
        }
      }
    }

    /**
     * @INFO Actualizar valor de un field con onChange
     * @param idx
     * @param idxField
     * @param e
     */
    const onChangeField = (idx: number, idField: string, e: any) => {
        const aux = {...settingTabs}
        const idxField = aux.tabs[idx].fields.findIndex(item => item.id === idField)
        if(idxField >= 0){
          aux.tabs[idx].fields[idxField].value = e.target.value
          // Validación de campos
          validateTabs(aux, idx)
        }
    }

    /**
     * @INFO Cambio de select de react (multiple)
     */
    const handleChangeReactSelect = (idxTab: number, idField: string, _data: IOptionSelect[]) => {
      const aux = {...settingTabs}
      const idxField = aux.tabs[idxTab].fields.findIndex(item => item.id === idField)
      if(idxField >= 0){
        aux.tabs[idxTab].fields[idxField].value = [..._data]
        // Validación de campos
        validateTabs(aux, idxTab)
      }
    }

    /**
     * @INFO Actualizar valor de un field con onChangeExerciseEnded
     * @param idx
     * @param idxField
     * @param data
     */
    const onChangeExerciseEnded = (idx: number, idxField: number, data: any, _invalid?: boolean) => {
        const aux = {...settingTabs}
        aux.tabs[idx].fields[idxField].value = data
        // Validación de campos
        validateTabs(aux, idx, _invalid)
    }

    /**
     * @INFO Actualizar campo de rango de disponibilidad
     * @param idxTab
     * @param idxField
     * @param e
     */
    const onChangeAvailabilityRange = (idxTab: number, idField: string, e: any, type: 'date' | 'hour') => {
        const aux = {...settingTabs}
        const idxField = aux.tabs[idxTab].fields.findIndex(item => item.id === idField)
        if(idxField < 0)return
        let value = {...aux.tabs[idxTab].fields[idxField].value as IAvailabilityRange}
        if(type === 'date'){
            const dataAux = new Date(e.target.value)
            dataAux.setDate(dataAux.getDate()+1)
            // dataAux.setMonth()
            value = {
                ...value,
                [e.target.name]: dataAux,
            }
        }else{
            let dateAux: Date
            // @ts-ignore
            if(value[e.target.name]){
                // @ts-ignore
                dateAux = new Date(value[e.target.name])
            }else{
                dateAux = new Date()
            }
            dateAux.setHours(e.target.value.split(':')[0])
            dateAux.setMinutes(e.target.value.split(':')[1])
            value = {
                ...value,
                [e.target.name]: dateAux
            }
        }

        aux.tabs[idxTab].fields[idxField].value = value
        // Validación de campos
        validateTabs(aux, idxTab)
    }

    /**
     * @INFO Actualizar campo de configuración de un juego
     * @param idxTab
     * @param idxField
     * @param e
     */
    const onChangeGameConfig = (idxTab: number, idField: string, e: any) => {
        const aux = {...settingTabs}
        const idxField = aux.tabs[idxTab].fields.findIndex(item => item.id === idField)
        if(idxField < 0)return
        let value = {...aux.tabs[idxTab].fields[idxField].value as IGameConfigEditor}
        value = {
            algorithm: {
                ...value.algorithm,
                [e.target.name]: e.target.value,
            },
        }
        aux.tabs[idxTab].fields[idxField].value = value
        // Validación de campos
        validateTabs(aux, idxTab)
    }

    /**
     * @INFO Cambio del campo duración
     * @param idxTab
     * @param idxField
     * @param e
     */
    const onChangeDuration = (idxTab: number, idxField: number, e: any) => {
        const aux = {...settingTabs}
        const inputValue: string = e.target.ValuePositional
        let seconds: number = getSecondsFromDuration(inputValue)
        aux.tabs[idxTab].fields[idxField].value = seconds
    }

    /**
     * @INFO Obtener segundos de texto
     * @param inputValue
     */
    const getSecondsFromDuration = (inputValue: string) => {
        const auxVector = inputValue.split(' ')
        let seconds: number = 0
        if(auxVector.length){
            auxVector.forEach(item => {
                if(item.includes('h')){
                    const hours = Number(item.replace('h', ''))
                    seconds += hours*3600
                }else if(item.includes('m')){
                    const minutes = Number(item.replace('m', ''))
                    seconds += minutes*60
                }else if(item.includes('s')){
                    seconds += Number(item.replace('s', ''))
                }
            })
        }
        return seconds
    }

    /**
     * @INFO Obtener duración con el formato
     * @param seconds
     */
    const getDurationFormated = (seconds: number) => {
        const hours = Math.trunc(seconds / 3600)
        const minutes = Math.trunc((seconds - (hours*3600))/60)
        const seconds2 = Math.trunc(seconds - (hours*3600) - (minutes*60))
        let response: string = ''
        if(hours){
            response = `${response} ${hours}h`
        }
        if(minutes){
            response = `${response} ${minutes}m`
        }
        if(seconds2){
            response = `${response} ${seconds2}s`
        }
        return response
    }

    /**
     * @INFO Actualizar el valor de un checkbox
     * @param idxTab
     * @param idxField
     */
    const handleClickCheckBox = (idxTab: number, idField: string, idGroup?: string) => {
        let aux = {...settingTabs}
        const idxField = aux.tabs[idxTab].fields.findIndex(item => item.id === idField)
        if(idxField < 0)return
        const value: boolean = aux.tabs[idxTab].fields[idxField].value as boolean
        if(value){
            aux.tabs[idxTab].fields[idxField].value = false
        }else{
            aux.tabs[idxTab].fields[idxField].value = true
        }
        // Actualizar el estado del switch del grupo
        if(idGroup){
          const idxGroup = aux.tabs[idxTab].groupsField.findIndex(item => item.id === idGroup)
          if(idxGroup >= 0){
            aux = validateSwitchStateGroup({
              idxTab,
              idGroup,
              _aux: {...aux}
            })
          }
        }
        // Validación de campos
        validateTabs(aux, idxTab)
    }

    /**
     * @INFO Subir un archivo
     * @param event
     */
    const handleUploadFile = (event: any, idx: number, idField: string) => {
        const aux = {...settingTabs}
        const files: File[] = event.target.files
        const idxField = aux.tabs[idx].fields.findIndex(item => item.id === idField)
        if(idxField < 0)return
        aux.tabs[idx].fields[idxField].value = files[0]
        aux.tabs[idx].fields[idxField].urlLocal = URL.createObjectURL(files[0])
        // Validación de campos
        validateTabs(aux, idx)
    }

    /**
     * @INFO Verificar la validación de los tabs
     * @param idx
     */
    const validateTabs = (data: ISettingsTabsEditorAcademicComponentState, idx?: number, _invalid?: boolean) => {
        const aux = {...data}
        let invalid = false
        if(idx !== undefined){
            invalid = validateFields(aux.tabs[idx].fields)
            aux.tabs[idx].invalid = _invalid ? _invalid : invalid
        }else{
            aux.tabs.forEach((tab, idx2) => {
                const currentTab = {...tab}
                const newInvalid = validateFields(currentTab.fields)
                currentTab.invalid = _invalid ? _invalid : newInvalid
                aux.tabs[idx2] = {...currentTab}
            })
        }
        // Validar botón de guardar todo
        validateSaveAll({...aux})
        // Enviar stream de toda la data
        sendStreamAllData({...aux})
        // Enviar stream de la data del tab
        sendStreamTabData({...aux}, idx)
        // Actualizar state con la validación
        updateSettingTabs({...aux})
    }

    /**
     * @INFO validar la información de los campos
     * @param fields
     */
    const validateFields = (fields: IFieldTabEditorState[]): boolean => {
        let invalid = false
        invalid = fields.reduce((acum: boolean, field, idxField) => {
            if(!field.required) return acum
            switch(field.type){
                case 'background':
                case 'icon':
                    if(typeof field.value === 'string'){
                        if(!(field.value as string).length){
                            acum = true
                            fields[idxField].invalid = true
                        }else{
                            fields[idxField].invalid = false
                        }
                    }else{
                        if((field.value as File) === undefined){
                            acum = true
                            fields[idxField].invalid = true
                        }else{
                            fields[idxField].invalid = false
                        }
                    }
                    break
                case 'availability_range':
                    if( !((field.value as any).start && (field.value as any).end) ){
                        acum = true
                        fields[idxField].invalid = true
                    }else{
                        fields[idxField].invalid = false
                    }
                    break
                case 'game':
                    if( !(Number((field.value as IGameConfigEditor)?.algorithm?.number_of_items) > 0 && Number((field.value as IGameConfigEditor)?.algorithm?.theoretical_probability) > 0) ){
                        acum = true
                        fields[idxField].invalid = true
                    }else{
                        fields[idxField].invalid = false
                    }
                    break
                default:
                  if(getDataTypeFieldEditor(field.type) === 'boolean'){
                    if(!((field.value as boolean) !== undefined)){
                        acum = true
                        fields[idxField].invalid = true
                    }else{
                        fields[idxField].invalid = false
                    }
                  }else{
                    if((field.value as string).length <= 0){
                        acum = true
                        fields[idxField].invalid = true
                    }else{
                        fields[idxField].invalid = false
                    }
                  }
                  break
            }
            return acum
        },false)
        return invalid
    }

    /**
     * @INFO Validar botón de guardar todo
     */
    const validateSaveAll = (data: ISettingsTabsEditorAcademicComponentState) => {
        const aux = {...data}
        const invalid = aux.tabs.reduce((acum: boolean, item) => {
            if(item.invalid){
                acum = true
            }
            return acum
        }, false)
        updateInvalidSaveAll(invalid)
    }

    /**
     * @INFO Enviar respuesta de save
     * @param idx
     * @param fn
     */
    const handleSaveClick = (idx: number, fn: TypeCallbackFunctionEditor) => {
        const aux = {...settingTabs}
        const responseJson = getJsonFromTabs(settingTabs ,idx)
        return fn(responseJson, {...aux.tabs[idx]})
    }

    /**
     * @INFO Click en guardar todo
     */
    const handleSaveAllClick = () => {
        if(props.onClickSaveAll){
            const responseJson = getJsonFromTabs(settingTabs)
            props.onClickSaveAll(responseJson, settingTabs)
        }
    }

    /**
     * @INFO Enviar stream de toda la data del panel
     * @param data
     */
    const sendStreamAllData = (data: ISettingsTabsEditorAcademicComponentState) => {
        if(props.streamAllData){
            const responseJson = getJsonFromTabs(data)
            props.streamAllData(responseJson)
        }
    }

    /**
     * @INFO Enviar stream de un tab particular
     * @param data
     * @param idx
     */
    const sendStreamTabData = (data: ISettingsTabsEditorAcademicComponentState, idx?: number) => {
        if(idx !== undefined && idx >= 0){
            const aux ={...data}
            const callback = aux.tabs[idx].streamCallback
            if(callback){
                const responseJson = getJsonFromTabs(aux , idx)
                callback(responseJson)
            }
        }
    }

    /**
     * @INFO Obtener Json de los tabs
     * @param idx
     */
    const getJsonFromTabs = (data: ISettingsTabsEditorAcademicComponentState , idx?: number) => {
        const aux = {...data}
        let responseJson: any = {}
        if(idx !== undefined && idx>=0){
            responseJson = aux.tabs[idx].fields.reduce((acum: Object, item) => {
                // @ts-ignore
                acum[item.keyApi] = item.value
                return acum
            }, {})
        }else{
            responseJson = aux.tabs.reduce((acumTab: Object, itemTab) => {
                const auxJson = itemTab.fields.reduce((acum: Object, item) => {
                    if(item.type === 'duration' || item.type === 'time_limit'){
                        // @ts-ignore
                        acum[item.keyApi] = getSecondsFromDuration(item.value as string)
                    } else if(item.type === 'components_related'){
                      // @ts-ignore
                      acum[item.keyApi] = typeof item.value !== 'string' && item.value?.length ? (item.value as IOptionSelect[])?.reduce((accum: string[], itemValue) => {
                        accum.push(itemValue.value)
                        return accum
                      },[]) : []
                    } else{
                        // @ts-ignore
                        acum[item.keyApi] = item.value
                    }
                    return acum
                }, {})
                // @ts-ignore
                acumTab[itemTab.type] = {...auxJson}
                return acumTab
            }, {})
        }
        return responseJson
    }

    // Estilos para abrir o cerrar el panel
    const styleOpen: CSSProperties = { width: '491px', transition: 'all 0.2s ease' }
    const styleClose: CSSProperties = { width: '60px', transition: 'all 0.2s ease' }

    /**
     * @INFO Handle para actualizar el color en el color picker
     * @param color
     */
    const handleChangePickerComplete = (color: any, idx: number, idField: string) => {
        const aux = {...settingTabs}
        const idxField = aux.tabs[idx].fields.findIndex(item => item.id === idField)
        if(idxField < 0)return
        aux.tabs[idx].fields[idxField].value = color.hex
        // Actualizar state
        updateSettingTabs({...aux})
        // Validación de campos
        validateTabs(aux, idx)
    }

    /**
     * @INFO Referencia al cambio de tabs
     */
    const handleChangeTab = (data: any) => {
        const aux = {...settingTabs}
        // Encontrar el tab
        const idxTab = aux.tabs.findIndex(item => item.id === data)
        if(idxTab >= 0){
            updateActiveTab({type: aux.tabs[idxTab].type, id: data })
        }
    }

    /**
     * @INFO Obtener la tab activa
     */
    const getActiveKey = () => {
        let key: any
        if(activeTab.id.length > 0){
            key = JSON.parse(JSON.stringify(activeTab.id))
        }else if(settingTabs.tabs.length > 0){
            key = JSON.parse(JSON.stringify(settingTabs.tabs[0].id))
        }else{
            key = undefined
        }
        return key
    }

    /**
     * @INFO Obtener value para input de tipo Date
     * @param date
     */
    const getValueFromDate = (date: Date, type: 'date' | 'hour') => {
        let newDate: Date
        let response = ''
        newDate = new Date(date)
        if(type === 'date'){
            const year = newDate.getFullYear()
            const month = newDate.getMonth()+1
            const day = newDate.getDate()
            response = `${year}-${month < 10 ? `0${month}` : month}-${day < 10 ? `0${day}` : day}`
            return response
        }else{
            response = `${newDate.getHours() < 10 ? `0${newDate.getHours()}` : newDate.getHours()}:${newDate.getMinutes() < 10 ? `0${newDate.getMinutes()}` : newDate.getMinutes()}`
            return response
        }

    }

    /**
     * @INFO Actualizar la pregunta actual
     * @param data
     */
    const updateCurrentQuestion = (_question?: IQuestionEditorState | IQuestionEditor ,data?: ISettingsTabsEditorAcademicComponentState) => {
        let aux: ISettingsTabsEditorAcademicComponentState
        if(data){
            aux ={...data}
        }else{
            aux ={...settingTabs}
        }
        if(_question){
            // Tipo panel
            const question = {..._question} as IQuestionEditorState
            // Encontrar el tab
            const idxTab = aux.tabs.findIndex(item => item.type === 'questions')
            if(idxTab >= 0){
                let questions = [...aux.tabs[idxTab].questions]
                // Poner las questions en no seleccionadas
                questions.forEach((item, idx) => {
                    questions[idx].selected = false
                    if(questions[idx].childs && questions[idx].childs?.length){
                        questions[idx].childs?.forEach((child, idxChild) => {
                            // @ts-ignore
                            questions[idx].childs[idxChild].selected = false
                        })
                    }
                })
                // Poner la pregunta en seleccionada
                if(question.question.parent){
                    // @INFO Si tiene padre lo pongo en seleccionado también
                    const idxParent = questions.findIndex(item => item.question._id === question.question.parent)
                    if(idxParent >= 0){
                        questions[idxParent].selected = true
                        const idxChild = questions[idxParent].childs?.findIndex(item => item.question._id === question.question._id)
                        if(idxChild !== undefined && idxChild >= 0){
                            // @ts-ignore
                            questions[idxParent].childs[idxChild].selected = true
                            // @ts-ignore
                            scrollToPositionEditor(questions[idxParent].childs[idxChild].question._id)
                        }
                    }
                }else{
                    const idxQuestion = questions.findIndex(item => item.question._id === question.question._id)
                    // Verifico si viene de respuesta o panel
                    if(question.id){
                        if(idxQuestion >= 0){
                            questions[idxQuestion].selected = true
                            scrollToPositionEditor(questions[idxQuestion].question._id)
                        }
                    }else{
                        if(idxQuestion >= 0){
                            questions[idxQuestion].selected = true
                            scrollToPositionEditor(questions[idxQuestion].question._id)
                        }else{
                            const newQuestion: IQuestionEditorState = {
                                ...question,
                                id: shortId.generate(),
                                selected: true,
                            }
                            scrollToPositionEditor(newQuestion.question._id)
                            questions = [...questions, newQuestion]
                        }
                    }
                }

                // Guardar las preguntas en el tab
                aux.tabs[idxTab].questions = [...questions]
                return aux
            }
        }
        return aux
    }

    /**
     * @INFO Actualizar el estado del grupo [Checked]
     * @param idxTab
     * @param idGroup
     * @returns
     */
    const handleClickSwitchGroup = (idxTab: number, idGroup: string, fields?: IFieldTabEditorState[]) => {
      const aux = {...settingTabs}
      const idxGroup = aux.tabs[idxTab].groupsField.findIndex(item => item.id === idGroup)
      let newState: boolean = false
      if(idxGroup < 0) return
      if(aux.tabs[idxTab].groupsField[idxGroup].state){
        newState = false
      }else{
        newState = true
      }
      // Colocar todos los campos booleanos en el estado que corresponda
      aux.tabs[idxTab].groupsField[idxGroup].state = newState
      if(fields){
        fields.forEach((field) => {
          if(getDataTypeFieldEditor(field.type) === 'boolean'){
            if(field.value !== newState){
              const idxField = aux.tabs[idxTab].fields.findIndex(item => item.id === field.id)
              if(idxField >= 0){
                aux.tabs[idxTab].fields[idxField].value = newState
              }
            }
          }
        })
      }
      updateSettingTabs({...aux})
    }

    /**
     * @INFO Actualizar el estado de open de un grupo de campos
     * @param idxTab
     * @param idGroup
     * @param state
     */
    const handleChangeOpenGroup = (idxTab: number, idGroup: string, state: boolean) => {
      const aux = {...settingTabs}
      const idxGroup = aux.tabs[idxTab].groupsField.findIndex(item => item.id === idGroup)
      if(idxGroup < 0) return
      aux.tabs[idxTab].groupsField[idxGroup].open = state
      updateSettingTabs({...aux})
    }

    /**
     * @INFO Click en un icono de los tab
     */
    const handleClickIconTab = () => {
      if(!props.open && props.onClickOpenPanel){
        props.onClickOpenPanel()
      }
    }

    return (
        <div className='academic_component_editor-component'>
            <div style={props.open ? styleOpen : styleClose} className='container-editor-subjects'>
                <div style={props.open ? styleOpen : styleClose}  className={`tabs-container-editor ${props.showSaveAll ? 'active-save-all' : 'disable-save-all'}`}>
                    {/* {props.open && !props.hideClose ? <AiOutlineClose onClick={props.handleClose} className='close-tabs-editor' /> :null} */}
                    <Tab.Container activeKey={ getActiveKey() } onSelect={handleChangeTab} >
                      <div className="container-sidebar">
                        {/* ================= Crear los iconos para los tabs ================ */}
                        <div className="container-icons-sidebar">
                          <Nav
                            variant='pills'
                          >
                            <Nav.Item>
                              <Nav.Link>
                              {props.open && !props.hideClose ?<AiOutlineClose onClick={props.handleClose} className='icon-tab' />: null}
                              </Nav.Link>
                            </Nav.Item>
                            {settingTabs.tabs.map((itemTab) => (
                              <Nav.Item
                                key={itemTab.id}
                              >
                                <Nav.Link
                                  eventKey={itemTab.id}
                                >
                                  <OverlayTrigger placement='left' overlay={<Tooltip id='tooltip_icon_sidebar_editor' >{t(`editor.editor_panel.tabs_names.${itemTab.type}`)}</Tooltip>} >
                                    {getIconFromTypeTabEdit(itemTab.type, colors, handleClickIconTab)}
                                  </OverlayTrigger>
                                </Nav.Link>
                              </Nav.Item>
                            ))}
                          </Nav>
                        </div>
                        {/* ================= Crear tabs del panel ========================== */}
                        <div className="container-body-sidebar">
                          <Tab.Content>
                            {
                                settingTabs.tabs.map((item, idx) => (
                                    <Tab.Pane
                                      key={item.id}
                                      eventKey={item.id}
                                    >
                                        {item.type !== 'questions' ?
                                          <>
                                            <div
                                              className='container-title-tab'
                                            >
                                                <p>{t(`editor.editor_panel.tabs_names.${item.type}`)}</p>
                                                { searchFieldFromListEditor(item.fields, 'active') ?
                                                    <div className="form-switch-active">
                                                      <label className='form-check-label'>
                                                          {t(`editor.editor_panel.fields.active`)}
                                                      </label>
                                                      <SwitchComponent
                                                        onClick={() => handleClickCheckBox(idx, searchFieldFromListEditor(item.fields, 'active')?.field.id as string)}
                                                        checked={searchFieldFromListEditor(item.fields, 'active')?.field.value as boolean}
                                                        disabled={props.disableAll}
                                                        size='large'
                                                      />
                                                  </div>
                                                : null}
                                            </div>
                                            {/* Renderizar los campos que no pertenecen a ningún grupo */}
                                            {filterFieldsByModuleEditor(item.fields, 'rest').map(itemField => (
                                              <RenderFieldByNameEditor
                                                key={itemField.id}
                                                field={itemField}
                                                disableAll={props.disableAll}
                                                disableEditDuration={props.disableEditDuration}
                                                idx={idx}
                                                disabledFields={disabledFields}
                                                courseModes={item.courseModes}
                                                onChangeField={onChangeField}
                                                onChangeAvailabilityRange={onChangeAvailabilityRange}
                                                getValueFromDate={getValueFromDate}
                                                onChangeGameConfig={onChangeGameConfig}
                                                handleClickCheckBox={handleClickCheckBox}
                                                handleUploadFile={handleUploadFile}
                                                handleChangePickerComplete={handleChangePickerComplete}
                                                onChangeReactSelect={handleChangeReactSelect}
                                              />
                                            ))}
                                          </>
                                        : null}
                                        {item.hideSaveButton || props.disableAll ?
                                          null
                                        :
                                          <button
                                            onClick={()=> handleSaveClick(idx, item.callback)}
                                            disabled={item.invalid || props.loading}
                                            className='btn btn-primary btn-save-editor'
                                          >
                                            {t('editor.editor_panel.save_button')}
                                          </button>
                                        }
                                    </Tab.Pane>
                                ))
                            }
                          </Tab.Content>
                        </div>
                      </div>

                    </Tab.Container>
                    {props.showSaveAll && !props.disableAll && activeTab.type !== 'questions' ? <button disabled={props.loading || invalidSaveAll || props.disableButtonSaveAll} onClick={handleSaveAllClick} className='btn btn-primary btn-save-editor btn-save-all-editor' >{t('editor.editor_panel.save_button')}</button> : null }
                    {props.loading ?<div className='loading-container'>
                        <Spinner className='spinner-loading' animation='border' />
                    </div>: null}
                </div>
            </div>
            <div className='container-children-academic-component-editor'>
                {props.children}
            </div>
        </div>
    )
}

export default AcademicComponentEditorLayout
